import type { PageFilter, GlobalColumnFilter, DateRangeState, DateRangeSelections } from "./pageFilters.js";
export type { DateRangeState, DateRangeSelections };

export type FilterSelections = Record<string, Set<string>>;
export type DynamicChartFilter = {
  id: string;
  sourceWidgetId: string;
  sourceColumn: string;
  value: string;
  label: string;
};

export function hasActiveFilterSelections(selections: FilterSelections): boolean {
  return Object.values(selections).some((set) => set.size > 0);
}

export function nextToggleFilterValue(
  prev: FilterSelections,
  filterId: string,
  value: string,
  multi: boolean
): FilterSelections {
  const current = prev[filterId] ?? new Set<string>();
  const next = new Set(current);
  if (multi) {
    if (next.has(value)) next.delete(value);
    else next.add(value);
  } else if (next.has(value) && next.size === 1) {
    next.clear();
  } else {
    next.clear();
    next.add(value);
  }
  return { ...prev, [filterId]: next };
}

export function nextSelectAllFilterValues(
  prev: FilterSelections,
  filterId: string,
  values: string[]
): FilterSelections {
  return { ...prev, [filterId]: new Set(values) };
}

export function nextClearFilterSelection(prev: FilterSelections, filterId: string): FilterSelections {
  return { ...prev, [filterId]: new Set<string>() };
}

export function nextClearAllFilterSelections(): FilterSelections {
  return {};
}

export function hasActiveDateRangeSelections(dateRangeSelections: DateRangeSelections): boolean {
  return Object.values(dateRangeSelections).some((s) => s.start !== null || s.end !== null);
}

export function nextSetDateRange(
  prev: DateRangeSelections,
  filterId: string,
  state: DateRangeState
): DateRangeSelections {
  return { ...prev, [filterId]: state };
}

export function nextClearDateRange(prev: DateRangeSelections, filterId: string): DateRangeSelections {
  return { ...prev, [filterId]: { start: null, end: null } };
}

export function nextClearAllDateRangeSelections(): DateRangeSelections {
  return {};
}

export function nextApplyChartDynamicFilter(
  prev: FilterSelections,
  allFilters: PageFilter[],
  sourceWidgetId: string,
  value: string
): FilterSelections {
  const sourceFilters = allFilters.filter((filter) => filter.source_widget_id === sourceWidgetId);
  if (sourceFilters.length === 0) return prev;
  const boundFilters = sourceFilters.filter((filter) => filter.bindings.length > 0);
  if (boundFilters.length === 0) return prev;

  const primary = boundFilters[0];
  const current = prev[primary.id];
  const alreadySelectedOnly =
    current &&
    current.size === 1 &&
    current.has(value) &&
    Object.entries(prev).every(([id, set]) => (id === primary.id ? set.size <= 1 : set.size === 0));

  if (alreadySelectedOnly) return {};

  const next: FilterSelections = {};
  for (const filter of allFilters) {
    next[filter.id] = new Set<string>();
  }
  for (const filter of boundFilters) {
    next[filter.id] = new Set<string>([value]);
  }
  return next;
}

function dynamicFilterId(sourceWidgetId: string, sourceColumn: string, value: string): string {
  return `${sourceWidgetId}::${sourceColumn}::${value}`;
}

export function nextToggleDynamicChartFilter(
  prev: DynamicChartFilter[],
  input: { sourceWidgetId: string; sourceColumn: string; value: string; label?: string }
): DynamicChartFilter[] {
  const id = dynamicFilterId(input.sourceWidgetId, input.sourceColumn, input.value);
  const exists = prev.some((filter) => filter.id === id);
  if (exists) {
    return prev.filter((filter) => filter.id !== id);
  }
  const label = input.label ?? `${input.sourceColumn}: ${input.value}`;
  return [...prev, { id, sourceWidgetId: input.sourceWidgetId, sourceColumn: input.sourceColumn, value: input.value, label }];
}

export function nextRemoveDynamicChartFilter(prev: DynamicChartFilter[], id: string): DynamicChartFilter[] {
  return prev.filter((filter) => filter.id !== id);
}

export function dynamicChartFiltersToColumnFilters(filters: DynamicChartFilter[]): GlobalColumnFilter[] {
  const byColumn = new Map<string, Set<string>>();
  for (const filter of filters) {
    let values = byColumn.get(filter.sourceColumn);
    if (!values) {
      values = new Set<string>();
      byColumn.set(filter.sourceColumn, values);
    }
    values.add(filter.value);
  }
  return Array.from(byColumn.entries()).map(([column, values]) => ({ column, values }));
}
